// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Mail, Phone, User, MapPin, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type CoachProspect = Database["public"]["Tables"]["coach_prospects"]["Row"];

const PIPELINE_STAGES = [
  { value: "prospect", label: "Prospect", color: "bg-gray-500" },
  { value: "trial_signup", label: "Trial Signup", color: "bg-blue-500" },
  { value: "email_verification", label: "Email Verified", color: "bg-yellow-500" },
  { value: "active_user", label: "Active User", color: "bg-green-500" },
  { value: "paid_subscriber", label: "Paid Subscriber", color: "bg-purple-500" },
  { value: "champion", label: "Champion", color: "bg-pink-500" },
  { value: "churned", label: "Churned", color: "bg-red-500" },
];

export const CoachAcquisitionPipeline: React.FC = () => {
  const [prospects, setProspects] = useState<CoachProspect[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase
        .from("coach_prospects")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (error) {
      console.error("Error fetching coach prospects:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProspectsByStage = (stage: string) => {
    return prospects.filter((prospect) => prospect.pipeline_stage === stage);
  };

  const updateProspectStage = async (prospectId: string, newStage: string) => {
    try {
      const { error } = await supabase
        .from("coach_prospects")
        .update({ pipeline_stage: newStage })
        .eq("id", prospectId);

      if (error) throw error;
      fetchProspects();
    } catch (error) {
      console.error("Error updating prospect stage:", error);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[...Array(7)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-16 bg-gray-100 rounded"></div>
                <div className="h-16 bg-gray-100 rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {PIPELINE_STAGES.map((stage) => {
          const stageProspects = getProspectsByStage(stage.value);
          
          return (
            <Card key={stage.value} className="h-fit">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    {stage.label}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {stageProspects.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stageProspects.map((prospect) => (
                  <Card key={prospect.id} className="border border-gray-200 hover:border-primary-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm truncate">{prospect.coach_name}</h4>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <MoreHorizontal className="h-3 w-3" />
                          </Button>
                        </div>
                        
                        <div className="space-y-1 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{prospect.college_name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span>{prospect.sport}</span>
                            {prospect.division_level && (
                              <Badge variant="outline" className="text-xs px-1 py-0">
                                {prospect.division_level}
                              </Badge>
                            )}
                          </div>
                          {prospect.estimated_subscription_value && (
                            <div className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              <span>${prospect.estimated_subscription_value}/mo</span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-1 pt-1">
                          <Button variant="outline" size="sm" className="h-6 px-2 text-xs">
                            <Mail className="h-3 w-3 mr-1" />
                            Email
                          </Button>
                          {prospect.phone && (
                            <Button variant="outline" size="sm" className="h-6 px-2 text-xs">
                              <Phone className="h-3 w-3 mr-1" />
                              Call
                            </Button>
                          )}
                        </div>

                        {prospect.notes && (
                          <p className="text-xs text-gray-500 line-clamp-2">{prospect.notes}</p>
                        )}

                        <div className="flex gap-1 flex-wrap">
                          {PIPELINE_STAGES.map((nextStage) => {
                            if (nextStage.value === prospect.pipeline_stage) return null;
                            return (
                              <Button
                                key={nextStage.value}
                                variant="outline"
                                size="sm"
                                className="h-5 px-2 text-xs"
                                onClick={() => updateProspectStage(prospect.id, nextStage.value)}
                              >
                                Move to {nextStage.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};